import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Camera, RefreshCw, X, Check, RotateCcw, ImageUp, AlertTriangle } from 'lucide-react';

const CameraCapture = ({ onCapture, onClose }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [facingMode, setFacingMode] = useState('environment');
  const [error, setError] = useState(null);
  const [capturedUrl, setCapturedUrl] = useState(null);
  const [capturedFile, setCapturedFile] = useState(null);
  const [starting, setStarting] = useState(true);

  const supported = typeof navigator !== 'undefined' && navigator.mediaDevices && navigator.mediaDevices.getUserMedia;
  const insecure = typeof window !== 'undefined' && window.location.protocol === 'http:' && window.location.hostname !== 'localhost';

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    if (!supported) {
      setError('Camera not supported — please use file upload');
      setStarting(false);
      return;
    }
    setStarting(true);
    setError(null);
    stopStream();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
    } catch (e) {
      if (e && (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError')) {
        setError('Camera permission denied. Enable camera access in your browser settings and retry.');
      } else if (e && e.name === 'NotFoundError') {
        setError('No camera found on this device. Please use file upload.');
      } else {
        setError('Could not start the camera. Please use file upload.');
      }
    } finally {
      setStarting(false);
    }
  }, [facingMode, supported, stopStream]);

  useEffect(() => {
    if (!capturedUrl) startCamera();
    return () => stopStream();
    // eslint-disable-next-line
  }, [facingMode]);

  useEffect(() => () => stopStream(), [stopStream]);

  const capture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `capture_${Date.now()}.jpg`, { type: 'image/jpeg' });
      setCapturedFile(file);
      setCapturedUrl(URL.createObjectURL(blob));
      stopStream();
    }, 'image/jpeg', 0.92);
  };

  const retake = () => {
    if (capturedUrl) URL.revokeObjectURL(capturedUrl);
    setCapturedUrl(null);
    setCapturedFile(null);
    startCamera();
  };

  const usePhoto = () => {
    if (capturedFile) {
      onCapture(capturedFile);
      if (capturedUrl) URL.revokeObjectURL(capturedUrl);
    }
  };

  const onGallery = (e) => {
    const file = e.target.files?.[0];
    if (file) onCapture(file);
  };

  return (
    <div data-testid="camera-capture" className="space-y-3">
      {insecure && (
        <div className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded p-2">
          Camera requires HTTPS. On an insecure origin, capture may be blocked — use file upload instead.
        </div>
      )}

      {error ? (
        <div className="text-center py-6" data-testid="camera-error">
          <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
          <p className="text-sm text-slate-600">{error}</p>
          <label className="inline-flex items-center gap-2 mt-3 text-sm text-blue-600 cursor-pointer">
            <ImageUp className="w-4 h-4" /> Upload from gallery
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onGallery} data-testid="camera-gallery-fallback" />
          </label>
        </div>
      ) : capturedUrl ? (
        <div className="space-y-3" data-testid="camera-preview">
          <img src={capturedUrl} alt="Captured" className="w-full rounded-lg border border-slate-200 max-h-80 object-contain bg-slate-900" />
          <div className="flex gap-2">
            <Button type="button" variant="outline" className="flex-1" onClick={retake} data-testid="camera-retake-btn">
              <RotateCcw className="w-4 h-4 mr-1.5" /> Retake
            </Button>
            <Button type="button" className="flex-1" onClick={usePhoto} data-testid="camera-use-btn">
              <Check className="w-4 h-4 mr-1.5" /> Use this photo
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="relative rounded-lg overflow-hidden bg-slate-900" style={{ minHeight: 220 }}>
            <video ref={videoRef} playsInline muted className="w-full max-h-80 object-contain" data-testid="camera-video" />
            {starting && (
              <div className="absolute inset-0 flex items-center justify-center text-white text-sm">
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Starting camera…
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => setFacingMode((m) => (m === 'environment' ? 'user' : 'environment'))} data-testid="camera-switch-btn">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Switch
            </Button>
            <Button type="button" className="flex-1" onClick={capture} disabled={starting} data-testid="camera-capture-btn">
              <Camera className="w-4 h-4 mr-1.5" /> Capture
            </Button>
            <Button type="button" variant="ghost" onClick={() => { stopStream(); onClose && onClose(); }} data-testid="camera-cancel-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>
          <label className="block text-center text-xs text-blue-600 cursor-pointer">
            or upload from gallery
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onGallery} data-testid="camera-gallery-input" />
          </label>
        </div>
      )}
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};

export default CameraCapture;
